import React, { PureComponent } from 'react';
import ReactQueryBuilder, { findPath } from 'react-querybuilder';
import { IconButton } from '@mui/material';
import { Add as AddIcon, Close, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { find, debounce, isEmpty } from 'lodash';

import {
  combinators,
  operatorsByType,
  levelsColors,
  defaultOperatorByType,
  CONFIGURATIONS_FIELD_NAME,
  EMPTY_OPTIONS,
  OPERATOR_NAMES,
} from './constants';
import StringUtils from '../../../services/StringUtils';
import Select from '../../Select/Select';
import MenuItem from '../../MenuItem/MenuItem';
import Button from '../../Button/Button';
import { OperatorSelector } from './OperatorSelector';
import { ChargerComponentOperatorSelector } from './ChargerComponentOperatorSelector';
import ValueEditorSelect from './ValueEditorSelect';
import { ValueEditorDate } from './ValueEditorDate';
import { ValueEditorChargerComponent } from './ValueEditorChargerComponent';
import { ValueEditorAutocomplete } from './ValueEditorAutocomplete';
import CombinatorSelector from './CombinatorSelector';

import './_styles.scss';
import Tooltip from '../../Tooltip';
import TextField from '../../TextField/TextField';
import { nodeConfigurationsValues } from '../utils';
import { ValueEditorText } from './ValueEditorText';

const conditionInputStyle = {
  width: 250,
  minWidth: 250,
  height: 33,
  marginRight: 10,
  display: 'flex',
  justifyContent: 'center',
};

class QueryBuilder extends PureComponent {
  state = {
    rulesState: [],
  };

  addRule = (newRule) => {
    this.setState((state) => ({
      rulesState: [
        ...state.rulesState,
        {
          groupId: newRule.id,
          hiddenButtons: [],
        },
      ],
    }));
  };

  removeRule = (ruleForRemove) => {
    this.setState((state) => ({
      rulesState: [...state.rulesState.filter(({ groupId }) => !ruleForRemove.some(({ groupId: removedId }) => groupId === removedId))],
    }));
  };

  updateRule = (group) => {
    this.setState((state) => ({
      rulesState: [
        ...state.rulesState.map(({ groupId }) => {
          const itemGroup = group.find((groupItem) => groupItem.id === groupId);
          const buttons = itemGroup.rules.map(({ field }) => field);

          return {
            groupId,
            hiddenButtons: [...new Set(buttons)],
          };
        }),
      ],
    }));
  };

  handleGroupState = (group) => {
    const isNewRule = group.find(({ id }) => !this.state.rulesState.some(({ groupId }) => groupId === id));

    if (isNewRule) {
      this.addRule(isNewRule);

      return;
    }

    const groupForRemoved = this.state.rulesState.filter(({ groupId }) => !group.some(({ id }) => id === groupId));

    if (groupForRemoved.length > 0) {
      this.removeRule(groupForRemoved);

      return;
    }

    this.updateRule(group);
  };

  onQueryChange = debounce((query) => {
    const { configurationBuilder, onChange } = this.props;

    configurationBuilder && this.handleGroupState(query.rules);
    onChange(query);
  }, 50);

  getOperators = (field) => {
    const currentField = find(this.props.fields, { name: field });

    if (!currentField) return;

    return operatorsByType[currentField.valueEditorType];
  };

  getDefaultOperator = (field) => {
    const currentField = find(this.props.fields, { name: field });

    if (!currentField) return;

    return defaultOperatorByType[currentField.valueEditorType];
  };

  renderFieldSelector = ({ options, value, level, handleOnChange, path }) => {
    const { combinator } = findPath(path.slice(0, -1), this.props.query);
    const borderColor = levelsColors[level % levelsColors.length];

    return (
      <div className="query-builder-field-selector">
        <div className="query-builder-offset-line" style={{ borderColor }} />
        <div className="query-builder-combinator-indicator">{combinator}</div>
        <Select value={value} style={conditionInputStyle} onChange={(event) => handleOnChange(event.target.value)}>
          {options.map(({ name, label }) => (
            <MenuItem key={name} value={name} title={label}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  };

  renderCombinatorSelector = ({ ...props }) => <CombinatorSelector {...props} query={this.props.query} />;

  renderOperatorSelector = ({ field, options, value, handleOnChange, configurationBuilder }) => {
    const fieldType = find(this.props.fields, { name: field }).valueEditorType;
    const currentField = find(this.props.fields, { name: field });
    const isSelectEmpty = isEmpty(currentField.values) && currentField.valueEditorType === 'select';
    const updatedValue = isSelectEmpty && !EMPTY_OPTIONS.includes(value) ? EMPTY_OPTIONS[0] : value;

    if (!currentField) return;

    const currentOptions =
      configurationBuilder && field === CONFIGURATIONS_FIELD_NAME.nodeName
        ? options.filter(({ name }) => ![
            OPERATOR_NAMES.noneOf,
            OPERATOR_NAMES.anyOf,
            OPERATOR_NAMES.empty,
            OPERATOR_NAMES.notEmpty
          ].includes(name))
        : options;

    if (fieldType === 'chargerPackage') {
      return (
        <div className="query-builder-operator-selector charger-component-selector">
          <ChargerComponentOperatorSelector fieldName={field} value={value} options={options} onChange={handleOnChange} />
        </div>
      );
    }

    return (
      <div className="query-builder-operator-selector">
        <OperatorSelector
          fieldName={field}
          fieldType={fieldType}
          value={updatedValue}
          style={conditionInputStyle}
          onChange={handleOnChange}
          options={currentOptions}
          fields={this.props.fields}
        />
      </div>
    );
  };

  renderValueEditor = ({ value, field, operator, handleOnChange }) => {
    const { valueEditorType, values } = find(this.props.fields, { name: field });
    const isSelect = valueEditorType === 'select';
    const disabled = EMPTY_OPTIONS.includes(operator);

    if (isSelect) {
      const updatedOperator = isEmpty(values) && !EMPTY_OPTIONS.includes(value) ? 'EMPTY' : operator;
      const isSelectDisabled = EMPTY_OPTIONS.includes(updatedOperator);

      return (
        <div className="query-builder-operator-selector">
          <ValueEditorSelect
            operator={updatedOperator}
            options={values}
            value={value}
            style={conditionInputStyle}
            onChange={handleOnChange}
            disabled={isSelectDisabled}
          />
        </div>
      );
    }

    if (valueEditorType === 'date') {
      return <ValueEditorDate operator={operator} value={value} onChange={handleOnChange} disabled={disabled} />;
    }

    if (valueEditorType === 'chargerPackage') {
      return (
        <ValueEditorChargerComponent
          operator={StringUtils.isJson(operator) ? JSON.parse(operator) : defaultOperatorByType.chargerPackage}
          value={value}
          onChange={handleOnChange}
        />
      );
    }

    if (valueEditorType === 'autocomplete') {
      return (
        <ValueEditorAutocomplete
          field={field}
          value={value}
          inputStyle={conditionInputStyle}
          onChange={handleOnChange}
          disabled={disabled}
        />
      );
    }

    value = operator === OPERATOR_NAMES.empty || operator === OPERATOR_NAMES.notEmpty ? '' : value;

    return <ValueEditorText value={value} style={conditionInputStyle} onChange={handleOnChange} disabled={disabled} />;
  };

  renderAddRuleButton = ({ level, handleOnClick }) => {
    const borderColor = levelsColors[level % levelsColors.length];

    return (
      <div className="query-builder-add-rule-btn">
        <div className="query-builder-offset-line" style={{ borderColor }} />
        <Button size="small" onClick={handleOnClick}>
          <AddIcon /> Add Condition
        </Button>
      </div>
    );
  };

  renderRemoveRuleButton = ({ handleOnClick }) => (
    <IconButton className="query-builder-remove-rule-btn" size="small" onClick={handleOnClick}>
      <Close fontSize="small" />
    </IconButton>
  );

  renderAddGroupButton = ({ handleOnClick }) => (
    <Button className="query-builder-add-group-btn" size="small" onClick={handleOnClick}>
      <AddIcon />
      Add Condition Group
    </Button>
  );

  renderAddConfigurationButton = ({ handleOnClick }) => {
    const borderColor = levelsColors[0];

    return (
      <div className="query-builder-add-rule-btn query-builder-add-group-btn__configuration-container">
        <div className="query-builder-offset-line" style={{ borderColor }} />

        <Button
          className="query-builder-add-group-btn query-builder-add-group-btn__configuration"
          size="small"
          onClick={handleOnClick}
          disabled={this.props.query.rules.filter((rule) => rule.combinator).length >= 5}
        >
          <AddIcon />
          Add Configuration Parameter Condition
        </Button>
        <Tooltip title="You can select maximum of 5 Condition Groups for this search.">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </div>
    );
  };

  renderGroupControlsComponent = ({ handleOnClick }) => (
    <IconButton className="query-builder-remove-group-btn" size="large" onClick={handleOnClick}>
      <Close fontSize="small" />
    </IconButton>
  );

  renderFieldConfigurationSelector = ({ ...props }) => (
    <div className="query-builder-field-selector">
      <div className="query-builder-offset-line" />
      <TextField value={nodeConfigurationsValues[props.rule.field]} disabled style={{ height: '100%', padding: '3px 10px' }} />
    </div>
  );

  renderAddButtons = ({ handleOnClick, ...props }) => {
    const {
      ruleOrGroup: { rules },
    } = props;

    const hiddenButtons = rules.map(({ field }) => field) ?? [];

    const configurationControllerHandler = (e, name) => handleOnClick(e, name);

    return (
      <div className="query-builder-add-group-btns">
        {this.props.fields.map(({ name, label }) => {
          if (!hiddenButtons.includes(name)) {
            return (
              <Button key={name} size="small" onClick={(e) => configurationControllerHandler(e, name)}>
                <AddIcon /> {label}
              </Button>
            );
          }

          return null;
        })}
      </div>
    );
  };

  renderConfigurationCombinatorSelector = ({ level, options, ...props }) => {
    if (level > 0) {
      const onlyAndOption = [{ name: 'AND', label: 'All' }];

      return this.renderCombinatorSelector({ level, options: onlyAndOption, ...props });
    }

    return this.renderCombinatorSelector({ level, options, ...props });
  };

  onAddRuleHandler = (...props) => {
    const [rule, , , context] = props;

    const fieldType = this.props.fields.filter(({ name }) => name === context)[0].valueEditorType;

    return {
      ...rule,
      field: context,
      operator: defaultOperatorByType[fieldType],
      value: '',
    };
  };

  render() {
    const { query, fields, configurationBuilder } = this.props;

    const controlElements = configurationBuilder
      ? {
          addRuleAction: ({ level, handleOnClick, ...props }) => level > 0 && this.renderAddButtons({ handleOnClick, ...props }),
          addGroupAction: ({ level, ...props }) => level < 1 && this.renderAddConfigurationButton({ ...props }),
          fieldSelector: ({ ...props }) => this.renderFieldConfigurationSelector({ ...props }),
          combinatorSelector: ({ level, options, ...props }) => this.renderConfigurationCombinatorSelector({ level, options, ...props }),
          operatorSelector: ({ ...props }) => this.renderOperatorSelector({ configurationBuilder, ...props }),
        }
      : {
          addGroupAction: this.renderAddGroupButton,
          addRuleAction: this.renderAddRuleButton,
          fieldSelector: this.renderFieldSelector,
          operatorSelector: this.renderOperatorSelector,
          combinatorSelector: this.renderCombinatorSelector,
        };

    return (
      <div className="query-builder">
        <ReactQueryBuilder
          query={query}
          fields={fields}
          combinators={combinators}
          onQueryChange={this.onQueryChange}
          getOperators={this.getOperators}
          onAddRule={configurationBuilder ? this.onAddRuleHandler : undefined}
          getDefaultOperator={this.getDefaultOperator}
          controlElements={{
            ...controlElements,
            removeGroupAction: this.renderGroupControlsComponent,
            valueEditor: this.renderValueEditor,
            removeRuleAction: this.renderRemoveRuleButton,
          }}
          controlClassnames={{
            ruleGroup: 'rule-group-base',
          }}
        />
      </div>
    );
  }
}

export default QueryBuilder;
